// src/components/ConfigGate.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import LoadingInline from "./LoadingInline";
import { apiGet } from "../utils/apiClient";

import { useUserContext } from "../contexts/UserContext";
import { isFreePlanUser, isLightPlanUser, resolveCurrentPlan } from "../utils/plan";
import {
    isRmsStoppedFrom,
    clearVisitedRmsStopNotice,
    hasVisitedRmsStopNotice,
    markVisitedRmsStopNotice,
} from "../utils/rmsStop";

type Props = React.PropsWithChildren<{}>;

const ConfigGate: React.FC<Props> = ({ children }) => {
    const user = useUserContext();
    const location = useLocation();

    const isDemo = location.pathname.startsWith("/demo");

    const isFreePlan = useMemo(() => isFreePlanUser(user), [user]);
    const isLightPlan = useMemo(() => isLightPlanUser(user), [user]);
    const isRestrictedPlan = isFreePlan || isLightPlan;
    const resolvedPlan = useMemo(() => resolveCurrentPlan(user), [user]);
    const planResolved = resolvedPlan !== "";

    const [checked, setChecked] = useState(false);
    const [configured, setConfigured] = useState(false);
    const [checkedPath, setCheckedPath] = useState("");

    // RN-246: 同じ会社・同じプランで一度確認を通った人は、画面を移るときに確認の完了を待たずに中身を出す。
    // 確認そのものは今までどおり毎回裏で行い、通らなければその時点で設定画面へ移す。
    const passIdentity = [
        user.companyId,
        user.tenantId,
        resolvedPlan,
        user.superAdmin ? 1 : 0,
        user.superAdminGlobal ? 1 : 0,
        isDemo ? 1 : 0,
    ].join("|");
    const [passedIdentity, setPassedIdentity] = useState("");

    useEffect(() => {
        if (user.loading) return;

        if (user.tenantId === "ecx" && !planResolved) {
            console.log("[ConfigGate] waiting plan resolution", {
                path: location.pathname,
                tenantId: user.tenantId,
                resolvedPlan,
            });
            return;
        }

        let cancelled = false;

        const sleep = (ms: number) =>
            new Promise((resolve) => window.setTimeout(resolve, ms));

        const fetchConfigState = async () => {
            const [rmsRes, fiscalRes, statusRes] = await Promise.all([
                apiGet("/api/company/settings/rms"),
                apiGet("/api/company/settings/fiscal"),
                apiGet("/api/company/datafetch-status"),
            ]);

            if (!rmsRes.ok || !fiscalRes.ok) {
                console.log("[ConfigGate] api failed", {
                    rmsOk: rmsRes.ok,
                    rmsStatus: rmsRes.status,
                    fiscalOk: fiscalRes.ok,
                    fiscalStatus: fiscalRes.status,
                });
                throw new Error("settings api failed");
            }

            const rms = await rmsRes.json();
            const fiscal = await fiscalRes.json();

            const pm = rms.passMeta ?? {};
            const hasRmsPass =
                !!pm.rmsPassMeta1?.exists ||
                !!pm.rmsPassMeta2?.exists ||
                !!pm.rmsPassMeta3?.exists ||
                !!pm.rmsPassMeta4?.exists ||
                !!pm.rmsPassMeta5?.exists;

            const hasCsvUser = !!String(rms.csvUser ?? "").trim();
            const hasCsvPass = !!pm.csvPassMeta?.exists;
            const hasRmsUser = !!String(rms.rmsUser ?? "").trim();
            const hasFiscalMonth = fiscal.fiscalMonth != null;
            const csvRequirementOk = isRestrictedPlan || (hasCsvUser && hasCsvPass);

            const ok =
                hasRmsUser &&
                hasRmsPass &&
                csvRequirementOk &&
                hasFiscalMonth;

            let hasLoginAuthError = false;
            let loginAuthErrorMsg = "";

            if (statusRes.ok) {
                const status = await statusRes.json();
                const msg = String(status?.dataFetchErrLastMsg ?? "");
                loginAuthErrorMsg = msg;
                // RN-284: 止まっている店舗を飛ばした回の文でも、ログイン認証エラーとして扱う
                // RN-283: 文に関係なく、止まっている印（rmsAuthStatus=invalid）があれば初期設定へ移す
                //         （受注データの取得の認証・R-Login の画面の文で止まった店舗も含む）
                // 赤い警告（RmsStopBanner）・初期設定の注意書きと同じ決まり（rmsAuthStatus=valid なら文が残っていても止まっていない）
                hasLoginAuthError = isRmsStoppedFrom(status?.rmsAuthStatus, msg);
            }

            return {
                ok,
                hasLoginAuthError,
                loginAuthErrorMsg,
                debug: {
                    path: location.pathname,
                    companyId: user.companyId,
                    tenantId: user.tenantId,
                    resolvedPlan,
                    planResolved,
                    isFreePlan,
                    isLightPlan,
                    isRestrictedPlan,
                    rmsUser: String(rms.rmsUser ?? "").trim(),
                    hasRmsUser,
                    passMeta: pm,
                    hasRmsPass,
                    csvUser: String(rms.csvUser ?? "").trim(),
                    hasCsvUser,
                    csvPassMeta: !!pm.csvPassMeta?.exists,
                    hasCsvPass,
                    csvRequirementOk,
                    fiscalMonth: fiscal.fiscalMonth,
                    hasFiscalMonth,
                },
            };
        };

        (async () => {
            try {
                if (isDemo || user.superAdmin || user.superAdminGlobal) {
                    setConfigured(true);
                    setPassedIdentity(passIdentity);
                    return;
                }

                if (!user.companyId) {
                    console.log("[ConfigGate] waiting companyId", {
                        path: location.pathname,
                        companyId: user.companyId,
                    });

                    await sleep(800);
                    if (cancelled) return;

                    if (!user.companyId) {
                        setConfigured(false);
                        setPassedIdentity("");
                        return;
                    }
                }

                let result = await fetchConfigState();
                // RN-246: 裏で確認している間に別の画面へ移った場合、古い画面の結果は使わない（新しい画面の確認が決める）
                if (cancelled) return;

                console.log("[ConfigGate check:first]", {
                    ...result.debug,
                    ok: result.ok,
                    hasLoginAuthError: result.hasLoginAuthError,
                    loginAuthErrorMsg: result.loginAuthErrorMsg,
                });

                // RN-283: 止まっている間は、アクセスのたびに（タブを開いて最初の1回）初期設定を経由させる。
                //         経由したあとは、ほかのページも開ける（全ページの赤い警告＝ RmsStopBanner で知らせ続ける）
                if (result.hasLoginAuthError && !hasVisitedRmsStopNotice(user.companyId)) {
                    markVisitedRmsStopNotice(user.companyId);
                    setConfigured(false);
                    setPassedIdentity("");
                    setChecked(true);
                    setCheckedPath(location.pathname);
                    (window as any).__SETUP_MODE__ = 2;
                    return;
                }
                if (!result.hasLoginAuthError) clearVisitedRmsStopNotice(user.companyId);

                if (!result.ok) {
                    await sleep(500);
                    if (cancelled) return;

                    result = await fetchConfigState();
                    if (cancelled) return;

                    console.log("[ConfigGate check:retry]", {
                        ...result.debug,
                        ok: result.ok,
                        hasLoginAuthError: result.hasLoginAuthError,
                        loginAuthErrorMsg: result.loginAuthErrorMsg,
                    });

                    if (result.hasLoginAuthError && !hasVisitedRmsStopNotice(user.companyId)) {
                        markVisitedRmsStopNotice(user.companyId);
                        setConfigured(false);
                        setPassedIdentity("");
                        setChecked(true);
                        setCheckedPath(location.pathname);
                        (window as any).__SETUP_MODE__ = 2;
                        return;
                    }
                }

                if (!result.ok) {
                    console.log("[ConfigGate] redirect reason: setup=1", {
                        path: location.pathname,
                        missing: {
                            rmsUser: !result.debug.hasRmsUser,
                            rmsPass: !result.debug.hasRmsPass,
                            csvRequiredButMissing: !result.debug.csvRequirementOk,
                            fiscalMonth: !result.debug.hasFiscalMonth,
                        },
                    });
                }

                setConfigured(!!result.ok);
                setPassedIdentity(result.ok ? passIdentity : "");
            } catch (e) {
                console.log("[ConfigGate] exception", e);
                if (cancelled) return;
                setConfigured(false);
                setPassedIdentity("");
            } finally {
                if (!cancelled) {
                    setChecked(true);
                    setCheckedPath(location.pathname);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        user.loading,
        user.companyId,
        user.tenantId,
        user.superAdmin,
        user.superAdminGlobal,
        isDemo,
        location.pathname,
        isFreePlan,
        isLightPlan,
        isRestrictedPlan,
        resolvedPlan,
        planResolved,
        passIdentity,
    ]);

    // RN-246: 一度通った人（同じ会社・同じプラン）は、この画面の確認が終わる前でも中身を出す
    const passedBefore = configured && passedIdentity !== "" && passedIdentity === passIdentity;
    const ready = !user.loading && checked && (checkedPath === location.pathname || passedBefore);
    if (!ready) return <LoadingInline />;

    if (!configured) {
        if (location.pathname.includes("/settings")) {
            // 既に settings ページにいる場合、無限リダイレクトを避ける
            const currentSetup = new URLSearchParams(location.search).get("setup");
            const setup = (window as any).__SETUP_MODE__ === 2 ? "2" : "1";
            if (currentSetup === setup) {
                console.log("[ConfigGate] already on settings with correct setup param", {
                    path: location.pathname,
                    setup,
                });
                // RN-246: 下の通過の分岐と同じ形（key 付き）にそろえる。形が違うと、同じ画面のまま分岐をまたいだときに枠が余分に作り直される
                return <React.Fragment key={location.pathname}>{children ? children : <Outlet />}</React.Fragment>;
            }
        }

        if (!location.pathname.includes("/settings")) {
            sessionStorage.setItem(
                "needsInitialSetup.v1",
                JSON.stringify({ from: location.pathname + location.search, at: Date.now() })
            );
        }

        const settingsPath = isDemo ? "/demo/settings" : "/app/settings";
        const setup = (window as any).__SETUP_MODE__ === 2 ? "2" : "1";
        (window as any).__SETUP_MODE__ = undefined;

        console.log("[ConfigGate] navigate settings", {
            from: location.pathname + location.search,
            to: `${settingsPath}?setup=${setup}`,
            setup,
        });

        return (
            <Navigate
                to={`${settingsPath}?setup=${setup}`}
                replace
                state={{ from: location.pathname + location.search }}
            />
        );
    }

    sessionStorage.removeItem("needsInitialSetup.v1");

    console.log("[ConfigGate] pass", {
        path: location.pathname,
        configured,
    });

    // RN-246: 中の枠（AppShell）は、今までどおり画面を移るたびに作り直す（移るたびに取り直していた一覧などをそのままにするため）。
    // 今までは確認中に枠が外れることで作り直されていたので、待ちを無くした後も key で同じ作り直しを残す。
    return <React.Fragment key={location.pathname}>{children ? children : <Outlet />}</React.Fragment>;
};

export default ConfigGate;